import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';

import { api } from './client';

/** Matches AdminTeamView from backend TeamsAdminService. */
export interface AdminTeam {
  id: string;
  slug: string;
  name: string;
  kind: string;
  createdBy: string;
  createdAt: string;
  archivedAt: string | null;
  billingPlan: string;
  subscriptionStatus: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  /** Live occupied seats (active members) — the per-seat storage pool multiplier. */
  seatsOccupied: number;
  storageUsedBytes: number;
  limitOverrides: Record<string, unknown>;
  memberCount: number;
}

export interface AdminTeamsQuery {
  limit?: number;
  offset?: number;
  search?: string;
  billingPlan?: string;
  subscriptionStatus?: string;
  includeArchived?: boolean;
  kind?: 'regular' | 'personal';
}

export interface AdminTeamsResult {
  total: number;
  items: AdminTeam[];
}

function buildQS(q: AdminTeamsQuery): string {
  const sp = new URLSearchParams();
  if (q.limit !== undefined) sp.set('limit', String(q.limit));
  if (q.offset !== undefined) sp.set('offset', String(q.offset));
  if (q.search) sp.set('search', q.search);
  if (q.billingPlan) sp.set('billingPlan', q.billingPlan);
  if (q.subscriptionStatus) sp.set('subscriptionStatus', q.subscriptionStatus);
  if (q.includeArchived !== undefined) sp.set('includeArchived', String(q.includeArchived));
  if (q.kind) sp.set('kind', q.kind);
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export function adminTeamsKey(query: AdminTeamsQuery = {}): QueryKey {
  return ['admin', 'teams', query];
}
export function adminTeamKey(id: string): QueryKey {
  return ['admin', 'team', id];
}

export function useAdminTeams(query: AdminTeamsQuery) {
  return useQuery({
    queryKey: adminTeamsKey(query),
    queryFn: () => api<AdminTeamsResult>(`/api/admin/teams${buildQS(query)}`),
    placeholderData: (prev) => prev,
  });
}

export function useAdminTeam(id: string | undefined) {
  return useQuery({
    queryKey: id ? adminTeamKey(id) : ['admin', 'team', 'noop'],
    queryFn: () => {
      if (!id) throw new Error('team id required');
      return api<AdminTeam>(`/api/admin/teams/${id}`);
    },
    enabled: Boolean(id),
  });
}

export interface PatchLimitsInput {
  id: string;
  mode?: 'merge' | 'replace';
  overrides: {
    max_members?: number | null;
    max_projects?: number | null;
    storage_quota_bytes?: number;
    max_file_size_bytes?: number;
    features?: Record<string, boolean>;
  };
  reason: string;
}

export function usePatchTeamLimits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mode, overrides, reason }: PatchLimitsInput) =>
      api<AdminTeam>(`/api/admin/teams/${id}/limits`, {
        method: 'PATCH',
        body: { mode, overrides, reason },
      }),
    onSuccess: (team) => {
      qc.setQueryData(adminTeamKey(team.id), team);
      void qc.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });
}

export function useTransferOwnership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, newOwnerUserId, reason }: { id: string; newOwnerUserId: string; reason: string }) =>
      api<AdminTeam>(`/api/admin/teams/${id}/transfer-ownership`, {
        method: 'POST',
        body: { newOwnerUserId, reason },
      }),
    onSuccess: (team) => {
      qc.setQueryData(adminTeamKey(team.id), team);
      void qc.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });
}

export function useSoftDeleteTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api<AdminTeam>(`/api/admin/teams/${id}`, {
        method: 'DELETE',
        body: { reason },
      }),
    onSuccess: (team) => {
      qc.setQueryData(adminTeamKey(team.id), team);
      void qc.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });
}

// ── Members + Billing Roles ──────────────────────────────────────────────

export type BillingRole = 'owner' | 'admin' | 'viewer';

/** Mirrors AdminTeamMember from teams-admin.service.ts. */
export interface AdminTeamMember {
  userId: string;
  email: string;
  name: string;
  username: string;
  avatarUrl: string | null;
  teamRole: string;
  joinedAt: string;
  billingRole: BillingRole | null;
  billingRoleAssignedAt: string | null;
  billingRoleAssignedBy: string | null;
}

export function adminTeamMembersKey(teamId: string): QueryKey {
  return ['admin', 'team', teamId, 'members'];
}

export function useAdminTeamMembers(teamId: string | undefined) {
  return useQuery({
    queryKey: teamId ? adminTeamMembersKey(teamId) : ['admin', 'team', 'members', 'noop'],
    queryFn: () => {
      if (!teamId) throw new Error('team id required');
      return api<AdminTeamMember[]>(`/api/admin/teams/${teamId}/members`);
    },
    enabled: Boolean(teamId),
  });
}

export function useAssignBillingRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      teamId,
      userId,
      role,
      reason,
    }: {
      teamId: string;
      userId: string;
      role: BillingRole;
      reason: string;
    }) =>
      api<AdminTeamMember>(`/api/admin/teams/${teamId}/billing-roles/${userId}`, {
        method: 'PATCH',
        body: { role, reason },
      }),
    onSuccess: (_member, { teamId }) => {
      void qc.invalidateQueries({ queryKey: adminTeamMembersKey(teamId) });
    },
  });
}

export function useRemoveBillingRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      teamId,
      userId,
      reason,
    }: {
      teamId: string;
      userId: string;
      reason: string;
    }) =>
      api<{ accepted: true }>(`/api/admin/teams/${teamId}/billing-roles/${userId}`, {
        method: 'DELETE',
        body: { reason },
      }),
    onSuccess: (_res, { teamId }) => {
      void qc.invalidateQueries({ queryKey: adminTeamMembersKey(teamId) });
    },
  });
}
